import { Redirect, Route, Router as ReactRouter, Switch, useParams } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import AdvancedFiltersPanel from './components/filters/AdvancedFiltersPanel';
import AsyncOverlayContainer from './components/overlay/AsyncOverlayContainer';
import CreateTaskPanel from './components/taskForms/CreateTaskPanel';
import ManageQueuesPageAsync from './components/queues/ManageQueuesPageAsync';
import EditTaskPanelContainer from './containers/EditTaskPanelContainer';
// @ts-expect-error untyped
import LegacyBoardPage from './containers/pages/LegacyBoardPage';
// @ts-expect-error untyped
import RecommendationProxyPage from './containers/pages/RecommendationProxyPage';
// @ts-expect-error untyped
import Redirector from './containers/pages/Redirector';
// @ts-expect-error untyped
import TasksHomePage from './containers/pages/TasksHomePage';
import { useQueryParams } from './hooks/useQueryParams';
import { getDisplayName } from './utils/getDisplayName';
import { Metrics } from './tracking/metrics';
import { getTaskHistoryUrl, getViewManagementUrl } from './utils/links';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const withErrorBoundary = ({
  Component,
  boundaryName
}) => {
  function WithErrorBoundary(props) {
    return /*#__PURE__*/_jsx(ErrorBoundary, {
      boundaryName: boundaryName,
      children: /*#__PURE__*/_jsx(Component, Object.assign({}, props))
    });
  }
  WithErrorBoundary.displayName = `WithErrorBoundary(${getDisplayName(Component)})`;
  return WithErrorBoundary;
};
const withModalOverlay = Component => {
  function WithModalOverlay(props) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(AsyncOverlayContainer, Object.assign({}, props)), /*#__PURE__*/_jsx(Component, Object.assign({}, props))]
    });
  }
  WithModalOverlay.displayName = `WithModalOverlay(${getDisplayName(Component)})`;
  return WithModalOverlay;
};
const TasksHomeRoute = withErrorBoundary({
  Component: withModalOverlay(TasksHomePage),
  boundaryName: 'TasksHomePageRoute'
});
const ManageQueuesPageRoute = withErrorBoundary({
  Component: withModalOverlay(ManageQueuesPageAsync),
  boundaryName: 'ManageQueuesPageRoute'
});
const RecommendationProxyRoute = withErrorBoundary({
  Component: RecommendationProxyPage,
  boundaryName: 'RecommendationProxyRoute'
});
const NewTasksLegacyBoardRoute = withErrorBoundary({
  Component: withModalOverlay(LegacyBoardPage),
  boundaryName: 'NewTasksLegacyBoardRoute'
});
const RedirectorRoute = withErrorBoundary({
  Component: Redirector,
  boundaryName: 'RedirectorRoute'
});
export function _LegacyIdRedirectRoute() {
  const {
    pageType,
    viewId
  } = useParams();
  const {
    taskId
  } = useQueryParams();
  const pagePathSegment = pageType === 'board' ? '/board/' : '/';
  Metrics.counter('tasks legacy redirect', {
    pageType,
    hasViewId: Boolean(viewId).toString(),
    hasTaskId: Boolean(taskId).toString()
  }).increment();
  if (viewId) {
    return /*#__PURE__*/_jsx(Redirect, {
      to: `/view/${viewId}${taskId ? `/task/${taskId}` : ''}`
    });
  }
  if (taskId) {
    return /*#__PURE__*/_jsx(Redirect, {
      to: `${pagePathSegment}task/${taskId}`
    });
  }
  return /*#__PURE__*/_jsx(Redirect, {
    to: `${pagePathSegment}`
  });
}
function HyphenatedViewsToUnderscores({
  location
}) {
  Metrics.counter('tasks legacy hyphen redirect').increment();
  return /*#__PURE__*/_jsx(Redirect, {
    to: Object.assign({}, location, {
      pathname: location.pathname.replace('/view/due-today', '/view/due_today').replace('/view/most-engaged', '/view/most_engaged')
    })
  });
}
function TaskHistoryRedirect() {
  const {
    taskId
  } = useParams();
  window.location.href = getTaskHistoryUrl(taskId);
  return null;
}
export default function Router({
  history
}) {
  return /*#__PURE__*/_jsxs(ReactRouter, {
    history: history,
    children: [/*#__PURE__*/_jsxs(Switch, {
      children: [/*#__PURE__*/_jsx(Route, {
        path: ['/view/due-today*', '/view/most-engaged*'],
        exact: true,
        component: HyphenatedViewsToUnderscores
      }), /*#__PURE__*/_jsx(Route, {
        path: ['/view/:viewId', '/view/:viewId/task/:taskId', '/view/:viewId/filters', '/view/:viewId/reorder', '/view/:viewId/history/:taskId'],
        exact: true,
        component: TasksHomeRoute
      }), /*#__PURE__*/_jsx(Route, {
        path: ['/board', '/board/task/:taskId'],
        exact: true,
        component: NewTasksLegacyBoardRoute
      }), /*#__PURE__*/_jsx(Route, {
        path: "/tasks/from-recommendation/:recommendationId",
        exact: true,
        component: RecommendationProxyRoute
      }), /*#__PURE__*/_jsx(Route, {
        path: ['/tasks', '/tasks/view/:viewId', '/tasks/:pageType', '/tasks/:pageType/view/:viewId'],
        exact: true,
        component: _LegacyIdRedirectRoute
      }), /*#__PURE__*/_jsx(Route, {
        path: "/queues",
        component: ManageQueuesPageRoute
      }), /*#__PURE__*/_jsx(Route, {
        path: "/view/:viewId/queues/new",
        render: () => /*#__PURE__*/_jsx(Redirect, {
          to: "/queues/new"
        })
      }), /*#__PURE__*/_jsx(Route, {
        path: "/view/:viewId/queues/:queueId",
        render: () => /*#__PURE__*/_jsx(Redirect, {
          to: "/queues/:queueId"
        })
      }), /*#__PURE__*/_jsx(Route, {
        path: "/view/:viewId/queues",
        render: () => /*#__PURE__*/_jsx(Redirect, {
          to: "/queues"
        })
      }), /*#__PURE__*/_jsx(Route, {
        path: ['/', '/task/:taskId'],
        exact: true,
        component: RedirectorRoute
      }), /*#__PURE__*/_jsx(Route, {
        render: () => /*#__PURE__*/_jsx(Redirect, {
          to: "/"
        })
      })]
    }), /*#__PURE__*/_jsxs(Switch, {
      children: [/*#__PURE__*/_jsx(Route, {
        path: ['/view/:viewId/task/new', '/board/task/new', '/task/new'],
        component: CreateTaskPanel
      }), /*#__PURE__*/_jsx(Route, {
        path: ['/view/:viewId/task/:taskId', '/board/task/:taskId', '/task/:taskId'],
        component: EditTaskPanelContainer
      }), /*#__PURE__*/_jsx(Route, {
        path: "/view/:viewId/filters",
        component: AdvancedFiltersPanel
      }), /*#__PURE__*/_jsx(Route, {
        path: ['views', '/view/:viewId/reorder', '/views/reorder'],
        render: () => {
          window.location.href = getViewManagementUrl();
          return null;
        }
      }), /*#__PURE__*/_jsx(Route, {
        path: "/view/:viewId/history/:taskId",
        component: TaskHistoryRedirect
      })]
    })]
  });
}